import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useFetchUserProgramDetail } from "@core/features/program";
import { usePlatformStore } from "@platform/store";

/**
 * Hook for fetching and computing derived program data.
 * Uses URL params to get programId and fetches via React Query.
 * React Query deduplicates calls, so multiple components can use this hook
 * without causing multiple network requests.
 */
export function useProgram() {
  const { id: programId } = useParams<{ id: string }>();
  const user = usePlatformStore((s) => s.user);
  const isHydrated = usePlatformStore((s) => s.isHydrated);

  const { data, isLoading } = useFetchUserProgramDetail({
    programId,
    user,
    isHydrated,
  });

  const program = data?.program ?? null;

  const phases = useMemo(
    () => program?.phases?.filter((phase) => phase.frontend_display) || [],
    [program?.phases]
  );

  const currentPhase = useMemo(
    () => phases.find((phase) => phase.is_current),
    [phases]
  );

  return { program, phases, currentPhase, isLoading };
}

export default useProgram;

